import { apiClient } from '../apiClient';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface CompiledUserSettings {
  version: 1;
  userId: string;
  updatedAtUtc: string;
  theme: {
    mode: ThemeMode;
  };
  notifications: {
    alarmSoundMuted: boolean;
  };
  sidebar: {
    admin: boolean;
    csh: boolean;
    alarm: boolean;
    help: boolean;
    addedCsh: boolean;
    cshGroupVisibility: Record<string, boolean>;
  };
}

export interface BuildUserSettingsInput {
  userId: string;
  isDarkTheme: boolean;
  isMuted: boolean;
  sidebar: {
    admin: boolean;
    csh: boolean;
    alarm: boolean;
    help: boolean;
    addedCsh: boolean;
    cshGroupVisibility: Record<number, boolean>;
  };
}

export interface UpdateUserSettingsRequest {
  version: number;
  theme: {
    mode: ThemeMode;
  };
  notifications: {
    alarmSoundMuted: boolean;
  };
  sidebar: {
    admin: boolean;
    csh: boolean;
    alarm: boolean;
    help: boolean;
    addedCsh: boolean;
    cshGroupVisibility: Record<string, boolean>;
  };
}

const STORAGE_PREFIX = 'smartAlarms.userSettings.v1';
const SETTINGS_API_PATH = '/api/Configuration/user-settings';

const normalizeThemeMode = (mode: unknown): ThemeMode => {
  if (mode === 'light' || mode === 'dark' || mode === 'system') {
    return mode;
  }
  return 'light';
};

const normalizeSettings = (input: unknown, fallbackUserId: string): CompiledUserSettings | null => {
  if (!input || typeof input !== 'object') {return null;}
  const parsed = input as Partial<CompiledUserSettings>;
  if (parsed?.version !== 1) {return null;}
  if (!parsed.theme || !parsed.notifications || !parsed.sidebar) {return null;}

  return {
    version: 1,
    userId: parsed.userId || fallbackUserId,
    updatedAtUtc: parsed.updatedAtUtc || new Date(0).toISOString(),
    theme: {
      mode: normalizeThemeMode(parsed.theme.mode),
    },
    notifications: {
      alarmSoundMuted: !!parsed.notifications.alarmSoundMuted,
    },
    sidebar: {
      admin: !!parsed.sidebar.admin,
      csh: !!parsed.sidebar.csh,
      alarm: !!parsed.sidebar.alarm,
      help: !!parsed.sidebar.help,
      addedCsh: parsed.sidebar.addedCsh !== false,
      cshGroupVisibility: parsed.sidebar.cshGroupVisibility || {},
    },
  };
};

export const themeModeToIsDark = (mode: ThemeMode): boolean => {
  if (mode === 'dark') {return true;}
  if (mode === 'light') {return false;}
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

export const getSettingsStorageKey = (userId: string): string => `${STORAGE_PREFIX}:${userId || 'anonymous'}`;

export const buildUserSettingsPayload = (input: BuildUserSettingsInput): CompiledUserSettings => {
  let mode: ThemeMode = 'light';
  if (input.isDarkTheme) {
    mode = 'dark';
  }

  return {
    version: 1,
    userId: input.userId || 'anonymous',
    updatedAtUtc: new Date().toISOString(),
    theme: {
      mode,
    },
    notifications: {
      alarmSoundMuted: input.isMuted,
    },
    sidebar: {
      admin: input.sidebar.admin,
      csh: input.sidebar.csh,
      alarm: input.sidebar.alarm,
      help: input.sidebar.help,
      addedCsh: input.sidebar.addedCsh,
      cshGroupVisibility: Object.entries(input.sidebar.cshGroupVisibility || {}).reduce((acc, [groupId, visible]) => {
        acc[String(groupId)] = !!visible;
        return acc;
      }, {} as Record<string, boolean>),
    },
  };
};

export const saveUserSettings = (payload: CompiledUserSettings): void => {
  if (typeof window === 'undefined') {return;}
  localStorage.setItem(getSettingsStorageKey(payload.userId), JSON.stringify(payload));
};

export const loadUserSettings = (userId: string): CompiledUserSettings | null => {
  if (typeof window === 'undefined') {return null;}
  const raw = localStorage.getItem(getSettingsStorageKey(userId));
  if (!raw) {return null;}
  try {
    return normalizeSettings(JSON.parse(raw), userId);
  } catch {
    return null;
  }
};

export const fetchUserSettingsFromApi = async (fallbackUserId: string): Promise<CompiledUserSettings | null> => {
  try {
    const res = await apiClient.get<unknown>(SETTINGS_API_PATH);
    if (!res.success || !res.data) {return null;}

    const normalized = normalizeSettings(res.data, fallbackUserId);
    if (normalized) {
      saveUserSettings(normalized);
    }
    return normalized;
  } catch {
    return null;
  }
};

export const saveUserSettingsToApi = async (payload: CompiledUserSettings): Promise<boolean> => {
  const requestPayload: UpdateUserSettingsRequest = {
    version: payload.version,
    theme: {
      mode: payload.theme.mode,
    },
    notifications: {
      alarmSoundMuted: payload.notifications.alarmSoundMuted,
    },
    sidebar: {
      admin: payload.sidebar.admin,
      csh: payload.sidebar.csh,
      alarm: payload.sidebar.alarm,
      help: payload.sidebar.help,
      addedCsh: payload.sidebar.addedCsh,
      cshGroupVisibility: payload.sidebar.cshGroupVisibility || {},
    },
  };

  try {
    const res = await apiClient.put<unknown>(SETTINGS_API_PATH, requestPayload);
    return !!res.success;
  } catch {
    return false;
  }
};

export const toNumericGroupVisibilityMap = (input: Record<string, boolean>): Record<number, boolean> => {
  return Object.entries(input || {}).reduce((acc, [groupId, visible]) => {
    const parsedId = Number(groupId);
    if (Number.isFinite(parsedId)) {
      acc[parsedId] = !!visible;
    }
    return acc;
  }, {} as Record<number, boolean>);
};
